// CastLayoutCanvas.tsx — SVG 2D casting yard layout.
// No Y-flip: plan coords = SVG coords (same as FloorPlanCanvas).
// Back wall at top (small Y), Front at bottom (large Y).
// Crane at upper-left of usable area (usableMinX, usableMinY).
// Capacity rings and crane icon rendered LAST so they appear on top.

import { useRef, useState, useEffect, useMemo } from 'react';
import type { CastLayoutResult } from '../utils/castLayout';
import type { CastLayoutSettings, PickStep } from '../types/panelvar';
import type { InstalledPosition } from '../utils/installedPositions';
import { filteredRings } from '../utils/craneCapacity';

interface Props {
  result:               CastLayoutResult;
  slabCorners:          [number, number][];
  settings:             CastLayoutSettings;
  // Feature 2: install location arrows
  installedPositions?:  Map<string, InstalledPosition> | null;
  // Feature 1: drag/rotate overrides
  selectedPanelIds?:    Set<string>;
  onPanelSelect?:       (id: string | null) => void;
  onPanelMove?:         (id: string, cx: number, cy: number) => void;
  onPanelRotate?:       (id: string, newRotRad: number) => void;
  onPanelDrop?:         (id: string) => void;
  // Rubber band multi-select
  onRubberBandSelect?:  (ids: string[]) => void;
  // Feature 3: animation
  currentPickStep?:     PickStep | null;
  allPickSteps?:        PickStep[];
}

const MIN_ZOOM = 0.05;
const MAX_ZOOM = 20;

/** Inset a polygon by `dist` (positive = inward).
 *  Each edge is offset inward; new corners are edge-pair intersections.
 *  Works for convex and simple concave polygons (L-shapes, U-shapes, etc.).
 */
function insetPolygon(corners: [number, number][], dist: number): [number, number][] {
  const n = corners.length;
  if (n < 3) return corners;

  // Compute the signed area to determine winding (CCW vs CW).
  let area = 0;
  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n;
    area += corners[i][0] * corners[j][1];
    area -= corners[j][0] * corners[i][1];
  }
  const sign = area > 0 ? -1 : 1; // Y-down screen coords: CW-on-screen = positive area

  // For each edge, produce the inward-offset line (A·x + B·y = C).
  const lines: { A: number; B: number; C: number }[] = [];
  for (let i = 0; i < n; i++) {
    const j    = (i + 1) % n;
    const dx   = corners[j][0] - corners[i][0];
    const dy   = corners[j][1] - corners[i][1];
    const len  = Math.hypot(dx, dy);
    if (len < 1e-9) { lines.push({ A: 0, B: 0, C: 0 }); continue; }
    const nx = sign * dy / len;
    const ny = sign * -dx / len;
    const ox = corners[i][0] + dist * nx;
    const oy = corners[i][1] + dist * ny;
    lines.push({ A: nx, B: ny, C: nx * ox + ny * oy });
  }

  // Intersect consecutive offset lines to get new corners
  const result: [number, number][] = [];
  for (let i = 0; i < n; i++) {
    const l1 = lines[i];
    const l2 = lines[(i + 1) % n];
    const det = l1.A * l2.B - l2.A * l1.B;
    if (Math.abs(det) < 1e-9) {
      const j = (i + 1) % n;
      result.push([
        (corners[j][0] + corners[(j + 1) % n][0]) / 2,
        (corners[j][1] + corners[(j + 1) % n][1]) / 2,
      ]);
    } else {
      result.push([
        (l1.C * l2.B - l2.C * l1.B) / det,
        (l1.A * l2.C - l2.A * l1.C) / det,
      ]);
    }
  }
  return result;
}

function ringColor(capacityT: number): string {
  if (capacityT >= 150) return '#44bb66';
  if (capacityT >= 60)  return '#ddaa22';
  return '#dd4422';
}

export default function CastLayoutCanvas({
  result,
  slabCorners,
  settings,
  installedPositions,
  selectedPanelIds,
  onPanelSelect,
  onPanelMove,
  onPanelRotate,
  onPanelDrop,
  onRubberBandSelect,
  currentPickStep,
  allPickSteps,
}: Props) {
  const [zoom, setZoom] = useState(1);
  const [pan,  setPan]  = useState({ x: 0, y: 0 });
  const [rubberBand, setRubberBand] = useState<{ x1: number; y1: number; x2: number; y2: number } | null>(null);
  const svgRef       = useRef<SVGSVGElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const zoomRef = useRef(zoom);
  const panRef  = useRef(pan);
  useEffect(() => { zoomRef.current = zoom; }, [zoom]);
  useEffect(() => { panRef.current  = pan;  }, [pan]);
  const vbRef = useRef({ vbX: 0, vbY: 0, vbW: 600, vbH: 400 });

  // Per-panel drag state
  const dragPanelRef = useRef<{ panelId: string; offsetX: number; offsetY: number } | null>(null);
  // Group drag: initial positions for all selected panels when a multi-select drag starts
  const groupDragRef = useRef<Map<string, { cx: number; cy: number }> | null>(null);

  // World bounding box (plan coords — no Y inversion)
  const worldBB = useMemo(() => {
    let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
    for (const [x, y] of slabCorners) {
      if (x < minX) minX = x; if (x > maxX) maxX = x;
      if (y < minY) minY = y; if (y > maxY) maxY = y;
    }
    if (!Number.isFinite(minX)) return { minX: 0, maxX: 1440, minY: 0, maxY: 720 };
    const margin = 120;
    return { minX: minX - margin, maxX: maxX + margin, minY: minY - margin, maxY: maxY + margin };
  }, [slabCorners]);

  const rings = useMemo(() => filteredRings(settings.craneModel, 3), [settings.craneModel]);

  // Stable ref for onRubberBandSelect (avoids stale closure in DOM event handler)
  const onRubberBandSelectRef = useRef(onRubberBandSelect);
  useEffect(() => { onRubberBandSelectRef.current = onRubberBandSelect; }, [onRubberBandSelect]);

  // Stable ref for result (used in rubber band hit-test)
  const resultRef = useRef(result);
  useEffect(() => { resultRef.current = result; }, [result]);

  // Zoom/pan via wheel and drag
  useEffect(() => {
    const el        = svgRef.current;
    const container = containerRef.current;
    if (!el || !container) return;

    const ZOOM_FACTOR = 1.15;
    let dragOrigin: { sx: number; sy: number; px: number; py: number } | null = null;
    let rubberOrigin: { wx: number; wy: number } | null = null;

    function svgPoint(clientX: number, clientY: number) {
      const rect = el!.getBoundingClientRect();
      const { vbX, vbY, vbW, vbH } = vbRef.current;
      return {
        wx: vbX + ((clientX - rect.left) / rect.width)  * vbW,
        wy: vbY + ((clientY - rect.top)  / rect.height) * vbH,
      };
    }

    function applyZoom(newZoom: number, clientX: number, clientY: number) {
      newZoom = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, newZoom));
      const { wx, wy } = svgPoint(clientX, clientY);
      const factor = newZoom / zoomRef.current;
      setPan({ x: wx - (wx - panRef.current.x) * factor, y: wy - (wy - panRef.current.y) * factor });
      setZoom(newZoom);
    }

    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      applyZoom(zoomRef.current * (e.deltaY < 0 ? ZOOM_FACTOR : 1 / ZOOM_FACTOR), e.clientX, e.clientY);
    };

    const onPointerDown = (e: PointerEvent) => {
      if (e.button !== 0) return;
      // Don't start pan/rubber-band if clicking inside a panel group
      let t: Element | null = e.target as Element;
      while (t && t !== el) {
        if ((t as SVGElement).dataset?.panelId) return;
        t = t.parentElement;
      }

      if (e.shiftKey) {
        // Start rubber band selection
        const { wx, wy } = svgPoint(e.clientX, e.clientY);
        rubberOrigin = { wx, wy };
        setRubberBand({ x1: wx, y1: wy, x2: wx, y2: wy });
        el!.setPointerCapture(e.pointerId);
        return;
      }

      dragOrigin = { sx: e.clientX, sy: e.clientY, px: panRef.current.x, py: panRef.current.y };
      el!.setPointerCapture(e.pointerId);
      // Deselect panel when clicking empty canvas
      onPanelSelect?.(null);
    };

    const onPointerMove = (e: PointerEvent) => {
      if (rubberOrigin) {
        const { wx, wy } = svgPoint(e.clientX, e.clientY);
        setRubberBand({ x1: rubberOrigin.wx, y1: rubberOrigin.wy, x2: wx, y2: wy });
        return;
      }
      if (!dragOrigin) return;
      const { vbW, vbH } = vbRef.current;
      const rect = el!.getBoundingClientRect();
      setPan({
        x: dragOrigin.px - ((e.clientX - dragOrigin.sx) / rect.width)  * vbW,
        y: dragOrigin.py - ((e.clientY - dragOrigin.sy) / rect.height) * vbH,
      });
    };

    const onPointerUp = (e: PointerEvent) => {
      if (rubberOrigin) {
        const { wx: finalWx, wy: finalWy } = svgPoint(e.clientX, e.clientY);
        const minX = Math.min(rubberOrigin.wx, finalWx);
        const maxX = Math.max(rubberOrigin.wx, finalWx);
        const minY = Math.min(rubberOrigin.wy, finalWy);
        const maxY = Math.max(rubberOrigin.wy, finalWy);
        const selected = resultRef.current.placed.filter(pp => {
          const ew = Math.abs(Math.cos(pp.rotationRad)) * pp.w + Math.abs(Math.sin(pp.rotationRad)) * pp.h;
          const eh = Math.abs(Math.sin(pp.rotationRad)) * pp.w + Math.abs(Math.cos(pp.rotationRad)) * pp.h;
          const pMinX = pp.cx - ew / 2;
          const pMaxX = pp.cx + ew / 2;
          const pMinY = pp.cy - eh / 2;
          const pMaxY = pp.cy + eh / 2;
          return pMinX <= maxX && pMaxX >= minX && pMinY <= maxY && pMaxY >= minY;
        });
        onRubberBandSelectRef.current?.(selected.map(pp => pp.panel.id));
        rubberOrigin = null;
        setRubberBand(null);
        return;
      }
      dragOrigin = null;
    };

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'h' || e.key === 'H' || e.key === 'f' || e.key === 'F') {
        setZoom(1); setPan({ x: 0, y: 0 });
      }
    };

    el.addEventListener('wheel', onWheel, { passive: false });
    el.addEventListener('pointerdown', onPointerDown);
    el.addEventListener('pointermove', onPointerMove);
    el.addEventListener('pointerup', onPointerUp);
    container.addEventListener('keydown', onKey);
    return () => {
      el.removeEventListener('wheel', onWheel);
      el.removeEventListener('pointerdown', onPointerDown);
      el.removeEventListener('pointermove', onPointerMove);
      el.removeEventListener('pointerup', onPointerUp);
      container.removeEventListener('keydown', onKey);
    };
  }, [onPanelSelect]);

  // ViewBox in plan coords
  const { vbX, vbY, vbW, vbH } = useMemo(() => {
    const bw = worldBB.maxX - worldBB.minX;
    const bh = worldBB.maxY - worldBB.minY;
    const baseW = bw / zoom;
    const baseH = bh / zoom;
    const cx = (worldBB.minX + worldBB.maxX) / 2 + pan.x;
    const cy = (worldBB.minY + worldBB.maxY) / 2 + pan.y;
    const vbX = cx - baseW / 2;
    const vbY = cy - baseH / 2;
    vbRef.current = { vbX, vbY, vbW: baseW, vbH: baseH };
    return { vbX, vbY, vbW: baseW, vbH: baseH };
  }, [zoom, pan, worldBB]);

  const { placed, craneX, craneY, craneExclusionActual } = result;

  const baseFontSize  = Math.max(12, vbW / 60);
  const smallFontSize = baseFontSize * 0.65;

  const slabPoints = slabCorners.map(([x, y]) => `${x},${y}`).join(' ');

  function panelRotDeg(rotationRad: number) {
    return -rotationRad * 180 / Math.PI;
  }

  // Animation helpers
  const activePanelId = currentPickStep?.panelId ?? null;
  function isPanelDone(panelId: string): boolean {
    if (!currentPickStep || !allPickSteps) return false;
    const lastStep = [...allPickSteps].reverse().find((s: PickStep) => s.panelId === panelId && s.phase === 6);
    return lastStep !== undefined && lastStep.stepIndex < currentPickStep.stepIndex;
  }

  // SVG point helper (used by panel drag handlers)
  function svgToWorld(clientX: number, clientY: number) {
    const rect = svgRef.current!.getBoundingClientRect();
    const { vbX: vx, vbY: vy, vbW: vw, vbH: vh } = vbRef.current;
    return {
      wx: vx + ((clientX - rect.left) / rect.width)  * vw,
      wy: vy + ((clientY - rect.top)  / rect.height) * vh,
    };
  }

  return (
    <div
      ref={containerRef}
      tabIndex={-1}
      style={{ width: '100%', height: '100%', outline: 'none', cursor: 'grab', background: '#1a1f2e' }}
    >
      <svg
        ref={svgRef}
        width="100%"
        height="100%"
        viewBox={`${vbX} ${vbY} ${vbW} ${vbH}`}
        style={{ display: 'block', touchAction: 'none', userSelect: 'none' }}
      >
        <defs>
          <clipPath id="slab-ring-clip">
            <polygon points={slabPoints} />
          </clipPath>
        </defs>

        {/* ── 1. Slab polygon ────────────────────────────────────────────────── */}
        {slabCorners.length >= 3 && (
          <polygon points={slabPoints} fill="#c8c0b0" stroke="#888" strokeWidth={baseFontSize * 0.3} />
        )}

        {/* ── 2. Clearance zone outline — inset polygon follows all walls ─── */}
        {slabCorners.length >= 3 && (() => {
          const inset  = insetPolygon(slabCorners, settings.clearanceIn);
          const points = inset.map(([x, y]) => `${x},${y}`).join(' ');
          const labelPt = inset.reduce(
            (best, [x, y]) => (y < best[1] || (y === best[1] && x < best[0])) ? [x, y] : best,
            inset[0],
          );
          return (
            <>
              <polygon
                points={points}
                fill="none"
                stroke="#f0a030"
                strokeWidth={baseFontSize * 0.2}
                strokeDasharray={`${baseFontSize * 0.8} ${baseFontSize * 0.5}`}
              />
              <text
                x={labelPt[0] + baseFontSize * 0.4}
                y={labelPt[1] + baseFontSize * 1.2}
                fontSize={smallFontSize}
                fill="#f0a030"
                opacity={0.8}
              >
                5′ CLEAR
              </text>
            </>
          );
        })()}

        {/* ── 3. Panel rectangles ───────────────────────────────────────────── */}
        {placed.map(pp => {
          const degRot = panelRotDeg(pp.rotationRad);

          // Animation coloring
          const isActive  = activePanelId === pp.panel.id;
          const isDone    = isPanelDone(pp.panel.id);
          const isFuture  = activePanelId !== null && !isActive && !isDone;
          const fill      = isActive  ? '#ff8800'
                          : isDone    ? '#6a6a6a'
                          : isFuture  ? '#8888aa'
                          : (pp.isStacked ? '#cdc4b0' : '#e8dfc8');
          const fillOpacity = isDone || isFuture ? 0.4 : 1;

          const isSelected = selectedPanelIds?.has(pp.panel.id) ?? false;
          const border     = isSelected ? '#4da6ff'
                           : isActive   ? '#ff6600'
                           : (pp.isStacked ? '#996633' : '#555');
          const strokeWidth = isSelected ? baseFontSize * 0.4 : baseFontSize * 0.18;

          const weightLabel = (pp.weightTons * 2).toFixed(1) + 'k';
          const markLabel   = pp.panel.mpvar.panelMark || pp.panel.id.slice(-4);
          const badgeR      = Math.min(baseFontSize * 0.85, pp.w / 4, pp.h / 4);

          const fl = (x: number) => x - pp.w / 2;
          const fb = (y: number) => y - pp.h / 2;

          const voids = [
            ...pp.panel.rovar.filter(s => s.enabled).map(s => s.data),
            ...pp.panel.wdvar.filter(s => s.enabled).map(s => s.data),
            ...pp.panel.drvar.filter(s => s.enabled).map(s => s.data),
            ...pp.panel.dlvar.filter(s => s.enabled).map(s => s.data),
            ...pp.panel.sbvar.filter(s => s.enabled).map(s => s.data),
          ];

          return (
            <g
              key={pp.panel.id + '-' + pp.stackLevel}
              transform={`translate(${pp.cx},${pp.cy}) rotate(${degRot})`}
              data-panel-id={pp.panel.id}
              style={{ cursor: onPanelMove ? (isSelected ? 'move' : 'pointer') : undefined }}
              onPointerDown={e => {
                if (!onPanelMove && !onPanelSelect) return;
                if (e.button !== 0) return;
                onPanelSelect?.(pp.panel.id);
                if (!onPanelMove) return;
                const { wx, wy } = svgToWorld(e.clientX, e.clientY);
                dragPanelRef.current = {
                  panelId: pp.panel.id,
                  offsetX: wx - pp.cx,
                  offsetY: wy - pp.cy,
                };
                // If this panel is part of a multi-selection, capture initial positions of all selected panels
                if (selectedPanelIds && selectedPanelIds.size > 1 && selectedPanelIds.has(pp.panel.id)) {
                  const initMap = new Map<string, { cx: number; cy: number }>();
                  for (const placed of result.placed) {
                    if (selectedPanelIds.has(placed.panel.id)) {
                      initMap.set(placed.panel.id, { cx: placed.cx, cy: placed.cy });
                    }
                  }
                  groupDragRef.current = initMap;
                } else {
                  groupDragRef.current = null;
                }
                (e.currentTarget as SVGGElement).setPointerCapture(e.pointerId);
              }}
              onPointerMove={e => {
                if (!dragPanelRef.current || dragPanelRef.current.panelId !== pp.panel.id) return;
                const { wx, wy } = svgToWorld(e.clientX, e.clientY);
                const newCx = wx - dragPanelRef.current.offsetX;
                const newCy = wy - dragPanelRef.current.offsetY;
                if (groupDragRef.current) {
                  const initPos = groupDragRef.current.get(pp.panel.id);
                  if (initPos) {
                    const dx = newCx - initPos.cx;
                    const dy = newCy - initPos.cy;
                    for (const [id, pos] of groupDragRef.current) {
                      onPanelMove?.(id, pos.cx + dx, pos.cy + dy);
                    }
                  }
                } else {
                  onPanelMove?.(pp.panel.id, newCx, newCy);
                }
              }}
              onPointerUp={e => {
                if (dragPanelRef.current?.panelId === pp.panel.id) {
                  onPanelDrop?.(pp.panel.id);
                  dragPanelRef.current = null;
                  groupDragRef.current = null;
                  (e.currentTarget as SVGGElement).releasePointerCapture(e.pointerId);
                }
              }}
              onContextMenu={e => {
                e.preventDefault();
                if (onPanelRotate) onPanelRotate(pp.panel.id, pp.rotationRad + Math.PI / 2);
              }}
            >
              {pp.isStacked && (
                <rect x={-pp.w / 2 + 6} y={-pp.h / 2 + 6} width={pp.w} height={pp.h}
                  fill="#333" opacity={0.3} rx={4} />
              )}
              <rect x={-pp.w / 2} y={-pp.h / 2} width={pp.w} height={pp.h}
                fill={fill} fillOpacity={fillOpacity}
                stroke={border} strokeWidth={strokeWidth} rx={3} />

              {/* Void openings */}
              {voids.map((f, i) => (
                <rect key={`v-${i}`}
                  x={fl(f.x - f.width / 2)} y={fb(f.y - f.height / 2)}
                  width={f.width} height={f.height}
                  fill="#1a1a1a" stroke="#666" strokeWidth={baseFontSize * 0.08} opacity={0.82}
                />
              ))}
              {pp.panel.rbvar.filter(s => s.enabled).map((s, i) => (
                <circle key={`rb-${i}`}
                  cx={fl(s.data.x)} cy={fb(s.data.y)} r={s.data.diameter / 2}
                  fill="#1a1a1a" stroke="#666" strokeWidth={baseFontSize * 0.08} opacity={0.82}
                />
              ))}

              {/* Pick points — green circles with crosshair */}
              {pp.panel.ppvar.filter(s => s.enabled).map((s, i) => {
                const pcx = fl(s.data.x);
                const pcy = fb(s.data.y);
                const pr  = Math.min(baseFontSize * 0.55, pp.w / 8, pp.h / 8);
                return (
                  <g key={`pp-${i}`}>
                    <circle cx={pcx} cy={pcy} r={pr}
                      fill="#22aa44" opacity={0.9} stroke="#006622" strokeWidth={baseFontSize * 0.1} />
                    <line x1={pcx - pr * 0.6} y1={pcy} x2={pcx + pr * 0.6} y2={pcy}
                      stroke="#fff" strokeWidth={baseFontSize * 0.1} />
                    <line x1={pcx} y1={pcy - pr * 0.6} x2={pcx} y2={pcy + pr * 0.6}
                      stroke="#fff" strokeWidth={baseFontSize * 0.1} />
                  </g>
                );
              })}

              {/* Brace points — orange squares */}
              {pp.panel.bpvar.filter(s => s.enabled).map((s, i) => {
                const bx = fl(s.data.x);
                const by = fb(s.data.y);
                const bs = baseFontSize * 0.5;
                return (
                  <rect key={`bp-${i}`}
                    x={bx - bs / 2} y={by - bs / 2} width={bs} height={bs}
                    fill="#ff6600" opacity={0.85} stroke="#cc3300" strokeWidth={baseFontSize * 0.08}
                  />
                );
              })}

              <text x={0} y={-baseFontSize * 0.1}
                textAnchor="middle" dominantBaseline="middle"
                fontSize={baseFontSize * 1.1} fontWeight="700"
                fill={isDone || isFuture ? '#999' : '#1a1208'}>
                {weightLabel}
              </text>
              <text x={0} y={baseFontSize * 1.1}
                textAnchor="middle" dominantBaseline="middle"
                fontSize={smallFontSize} fill={isDone || isFuture ? '#888' : '#3a3020'}>
                {markLabel}
              </text>
              <circle cx={-pp.w / 2 + badgeR + 3} cy={-pp.h / 2 + badgeR + 3} r={badgeR}
                fill={isActive ? '#883300' : '#1a3a5c'} opacity={0.85} />
              <text x={-pp.w / 2 + badgeR + 3} y={-pp.h / 2 + badgeR + 3}
                textAnchor="middle" dominantBaseline="middle"
                fontSize={smallFontSize * 0.85} fontWeight="700" fill="#aad4ff">
                {pp.pickOrder}
              </text>
              {pp.isStacked && (
                <text x={pp.w / 2 - 4} y={-pp.h / 2 + smallFontSize}
                  textAnchor="end" fontSize={smallFontSize * 0.8} fill="#cc6600" fontWeight="700">
                  ×{pp.stackLevel}
                </text>
              )}
            </g>
          );
        })}

        {/* ── 3.5. Install location arrows ─────────────────────────────────── */}
        {installedPositions && placed.map(pp => {
          const inst = installedPositions.get(pp.panel.id);
          if (!inst) return null;
          const dx  = inst.x - pp.cx;
          const dy  = inst.y - pp.cy;
          const len = Math.hypot(dx, dy);
          if (len < 1) return null;
          const hue   = 120 - (pp.pickOrder / placed.length) * 120;
          const color = `hsl(${hue}, 80%, 55%)`;
          const ux = dx / len, uy = dy / len;
          const as = baseFontSize * 1.5;
          return (
            <g key={`arrow-${pp.panel.id}`} style={{ pointerEvents: 'none' }}>
              <line
                x1={pp.cx} y1={pp.cy} x2={inst.x} y2={inst.y}
                stroke={color} strokeWidth={baseFontSize * 0.18}
                strokeDasharray={`${baseFontSize * 0.6} ${baseFontSize * 0.3}`}
                opacity={0.75}
              />
              <polygon
                points={[
                  `${inst.x},${inst.y}`,
                  `${inst.x - ux * as - uy * as * 0.4},${inst.y - uy * as + ux * as * 0.4}`,
                  `${inst.x - ux * as + uy * as * 0.4},${inst.y - uy * as - ux * as * 0.4}`,
                ].join(' ')}
                fill={color} opacity={0.85}
              />
            </g>
          );
        })}

        {/* ── 4. Capacity rings — clipped to slab polygon ─────────────────── */}
        {settings.showCapacityRings && <g clipPath="url(#slab-ring-clip)">
        {rings.map(ring => {
          const color = ringColor(ring.capacityT);
          return (
            <g key={ring.radiusM}>
              <circle cx={craneX} cy={craneY} r={ring.radiusIn}
                fill={color} fillOpacity={0.06}
                stroke={color} strokeOpacity={0.5}
                strokeWidth={baseFontSize * 0.15}
                strokeDasharray={`${baseFontSize} ${baseFontSize * 0.5}`} />
              <text x={craneX + ring.radiusIn + baseFontSize * 0.3} y={craneY + smallFontSize * 0.35}
                fontSize={smallFontSize} fill={color} opacity={0.85}>
                {ring.capacityT * 2}k
              </text>
            </g>
          );
        })}
        </g>}

        {/* ── 5. Crane arm line (animation) ────────────────────────────────── */}
        {currentPickStep && (
          <line
            x1={craneX} y1={craneY}
            x2={craneX + Math.cos(currentPickStep.craneArmAngleRad) * currentPickStep.craneArmLengthIn}
            y2={craneY + Math.sin(currentPickStep.craneArmAngleRad) * currentPickStep.craneArmLengthIn}
            stroke="#eecc44"
            strokeWidth={baseFontSize * 0.3}
            strokeDasharray={`${baseFontSize} ${baseFontSize * 0.4}`}
            opacity={0.85}
            style={{ pointerEvents: 'none' }}
          />
        )}

        {/* ── 5.5. Destination ghost (phases 4–6) ──────────────────────────── */}
        {currentPickStep && currentPickStep.phase >= 4 && (() => {
          const pp = placed.find(p => p.panel.id === currentPickStep.panelId);
          if (!pp) return null;
          return (
            <rect
              x={currentPickStep.panelCx - pp.w / 2}
              y={currentPickStep.panelCy - pp.h / 2}
              width={pp.w} height={pp.h}
              fill="none"
              stroke="#4da6ff"
              strokeWidth={baseFontSize * 0.2}
              strokeDasharray={`${baseFontSize * 0.5} ${baseFontSize * 0.3}`}
              opacity={0.6}
              style={{ pointerEvents: 'none' }}
            />
          );
        })()}

        {/* ── 5.7. Brace indicators (phase 5 "Set & brace") ────────────────── */}
        {currentPickStep && currentPickStep.phase === 5 && (() => {
          const pp = placed.find(p => p.panel.id === currentPickStep.panelId);
          if (!pp) return null;
          const bs = baseFontSize * 0.7;
          return (
            <g style={{ pointerEvents: 'none' }}>
              {pp.panel.bpvar.filter(s => s.enabled).map((s, i) => {
                // Map panel-local brace coords to installed world position
                const degRot = panelRotDeg(pp.rotationRad);
                const rad    = -degRot * Math.PI / 180;
                const lx = s.data.x - pp.w / 2;
                const ly = s.data.y - pp.h / 2;
                const wx = currentPickStep.panelCx + lx * Math.cos(rad) - ly * Math.sin(rad);
                const wy = currentPickStep.panelCy + lx * Math.sin(rad) + ly * Math.cos(rad);
                return (
                  <rect key={`brace-anim-${i}`}
                    x={wx - bs / 2} y={wy - bs / 2} width={bs} height={bs}
                    fill="#ff6600" stroke="#cc3300" strokeWidth={baseFontSize * 0.15}
                    opacity={0.9}
                  />
                );
              })}
            </g>
          );
        })()}

        {/* ── 6. Crane icon — TOPMOST layer ───────────────────────────────── */}
        <g transform={`translate(${craneX},${craneY})`}>
          <line x1={0} y1={0} x2={0} y2={-baseFontSize * 4} stroke="#eecc44" strokeWidth={baseFontSize * 0.35} />
          <line x1={0} y1={-baseFontSize * 3} x2={baseFontSize * 5} y2={-baseFontSize * 5} stroke="#eecc44" strokeWidth={baseFontSize * 0.25} />
          <line x1={baseFontSize * 5} y1={-baseFontSize * 5} x2={baseFontSize * 5} y2={-baseFontSize * 1.5}
            stroke="#eecc44" strokeWidth={baseFontSize * 0.15}
            strokeDasharray={`${baseFontSize * 0.4} ${baseFontSize * 0.2}`} />
          <rect x={-baseFontSize * 1.5} y={-baseFontSize * 1} width={baseFontSize * 1.2} height={baseFontSize * 0.7}
            fill="#eecc44" opacity={0.9} />
          <circle cx={0} cy={0} r={baseFontSize * 0.6} fill="#eecc44" opacity={0.9} />
          <circle cx={0} cy={0} r={craneExclusionActual}
            fill="none" stroke="#eecc44" strokeOpacity={0.25}
            strokeWidth={baseFontSize * 0.12}
            strokeDasharray={`${baseFontSize * 0.5} ${baseFontSize * 0.3}`} />
          <text x={0} y={baseFontSize * 1.4} textAnchor="middle" fontSize={smallFontSize} fill="#eecc44" fontWeight="700">
            LR 1300
          </text>
        </g>

        {/* ── 7. Up direction indicator ─────────────────────────────────────── */}
        <g transform={`translate(${vbX + baseFontSize * 3},${vbY + vbH - baseFontSize * 5})`}>
          <circle cx={0} cy={0} r={baseFontSize * 2} fill="none" stroke="#cc2222" strokeWidth={baseFontSize * 0.15} />
          <polygon points={`0,${-baseFontSize * 1.5} ${-baseFontSize * 0.6},0 ${baseFontSize * 0.6},0`}
            fill="#cc2222" />
          <line x1={0} y1={0} x2={0} y2={baseFontSize * 0.9} stroke="#cc2222" strokeWidth={baseFontSize * 0.2} />
          <text x={0} y={-baseFontSize * 2.4}
            textAnchor="middle" fontSize={smallFontSize * 0.9} fill="#cc2222" fontWeight="700">
            ↑
          </text>
        </g>

        {/* ── 8. Rubber band selection rect ─────────────────────────────────── */}
        {rubberBand && (
          <rect
            x={Math.min(rubberBand.x1, rubberBand.x2)}
            y={Math.min(rubberBand.y1, rubberBand.y2)}
            width={Math.abs(rubberBand.x2 - rubberBand.x1)}
            height={Math.abs(rubberBand.y2 - rubberBand.y1)}
            fill="rgba(77,166,255,0.12)"
            stroke="#4da6ff"
            strokeWidth={2 / zoom}
            strokeDasharray={`${8 / zoom} ${4 / zoom}`}
            pointerEvents="none"
          />
        )}
      </svg>

      <div style={{ position: 'absolute', bottom: 8, right: 12, color: '#555', fontSize: 11, pointerEvents: 'none' }}>
        H = fit · scroll = zoom · drag = pan · right-click panel = rotate
      </div>
    </div>
  );
}
